import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Shield,
  Lock,
  Eye,
  Key,
  FileCheck,
  Users,
  CheckCircle2,
  ChevronDown,
  Mail,
  ArrowRight,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/cn';

export function SecurityPage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const certifications = [
    { key: 'soc2', icon: FileCheck, color: 'from-green-500 to-emerald-500' },
    { key: 'gdpr', icon: Shield, color: 'from-blue-500 to-cyan-500' },
    { key: 'ccpa', icon: Lock, color: 'from-purple-500 to-violet-500' },
    { key: 'hipaa', icon: Eye, color: 'from-orange-500 to-amber-500' },
  ];

  const features = [
    { key: 'encryption', icon: Lock },
    { key: 'zeroData', icon: Eye },
    { key: 'sso', icon: Key },
    { key: 'mfa', icon: Shield },
    { key: 'audit', icon: FileCheck },
    { key: 'scim', icon: Users },
  ];

  const practices = t('security.practices.items', { returnObjects: true }) as string[];
  const faqQuestions = [0, 1, 2];

  return (
    <>
      <SEO {...SEO_CONFIG.security} />
      <div className="min-h-screen">
      {/* Hero */}
      <section className="section-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <Shield size={16} />
              <span>{t('security.badge')}</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('security.title')}{' '}
              <span className="text-accent">{t('security.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-text-secondary">
              {t('security.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">{t('security.certifications.title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert) => (
              <div key={cert.key} className="card p-6 text-center hover:border-accent/30 transition-colors">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${cert.color} flex items-center justify-center mx-auto mb-4`}>
                  <cert.icon className="text-white" size={28} />
                </div>
                <h3 className="font-semibold mb-2">{t(`security.certifications.${cert.key}.title`)}</h3>
                <p className="text-text-secondary text-sm">{t(`security.certifications.${cert.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">{t('security.features.title')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {features.map((feature) => (
              <div key={feature.key} className="card p-6 hover:border-accent/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <feature.icon className="text-accent" size={24} />
                </div>
                <h3 className="font-semibold mb-2">{t(`security.features.${feature.key}.title`)}</h3>
                <p className="text-text-secondary text-sm">{t(`security.features.${feature.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practices */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-lg mb-8 text-center">{t('security.practices.title')}</h2>

            <div className="grid md:grid-cols-2 gap-4">
              {practices.map((practice, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-bg-card border border-border-default">
                  <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-text-secondary">{practice}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vulnerability Reporting */}
      <section className="section">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="card p-8 bg-gradient-to-br from-accent/10 to-purple-500/5 border-accent/20">
              <Mail className="mx-auto mb-4 text-accent" size={48} />
              <h2 className="heading-md mb-4">{t('security.vulnerability.title')}</h2>
              <p className="text-text-secondary mb-6">{t('security.vulnerability.description')}</p>
              <a
                href={`mailto:${t('security.vulnerability.email')}`}
                className="btn btn-primary"
              >
                {t('security.vulnerability.email')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-lg mb-8 text-center">{t('security.faq.title')}</h2>

            <div className="space-y-4">
              {faqQuestions.map((index) => (
                <div key={index} className="card overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold pr-4">{t(`security.faq.questions.${index}.q`)}</span>
                    <ChevronDown
                      size={20}
                      className={cn(
                        'text-text-tertiary transition-transform flex-shrink-0',
                        openFaq === index && 'rotate-180'
                      )}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6 pt-0">
                      <p className="text-text-secondary">{t(`security.faq.questions.${index}.a`)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-lg mb-4">{t('security.cta.title')}</h2>
            <p className="text-text-secondary mb-8">{t('security.cta.subtitle')}</p>
            <Link to="/contact" className="btn btn-primary">
              {t('security.cta.contact')}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
